import React, { useState } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import { apiUrl } from "../../utils/api";
import styles from "./ApplyModal.module.css";

const emptyForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  experience: "",
  coveringLetter: "",
};

const ApplyModal = ({ show, onHide, job }) => {
  const [form, setForm] = useState(emptyForm);
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const resetAndClose = () => {
    setForm(emptyForm);
    setCv(null);
    setError("");
    setSuccess(false);
    setSubmitting(false);
    onHide();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!job) return;
    if (!cv) {
      setError("Please attach your CV.");
      return;
    }

    const payload = new FormData();
    payload.append("jobId", job.jobId);
    payload.append("jobTitle", job.jobTitle || "");
    payload.append("category", job.category || "");
    payload.append("firstName", form.firstName.trim());
    payload.append("lastName", form.lastName.trim());
    payload.append("phone", form.phone.trim());
    payload.append("email", form.email.trim());
    payload.append("experience", form.experience.trim());
    payload.append("coveringLetter", form.coveringLetter.trim());
    payload.append("cv", cv);

    setSubmitting(true);
    try {
      const response = await fetch(apiUrl("/api/applications"), {
        method: "POST",
        body: payload,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Could not submit application.");
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={resetAndClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {success ? "Application received" : `Apply for ${job?.jobTitle || "this role"}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success ? (
          <div className={styles.successCard}>
            <div className={styles.successIcon} aria-hidden="true">
              <FaCheckCircle />
            </div>
            <h3>Thank you, {form.firstName || "applicant"}.</h3>
            <p>
              Your application for <strong>{job?.jobTitle}</strong> has been
              submitted successfully.
            </p>
            <div className={styles.successMeta}>
              <span>Job ID</span>
              <strong>{job?.jobId}</strong>
            </div>
            <p className={styles.successNext}>
              Our hiring team will review your profile, covering letter, and CV.
              If your experience is a match, we will contact you using the email
              and phone number you provided.
            </p>
            <Button className={styles.submit} type="button" onClick={resetAndClose}>
              Close
            </Button>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <p className={styles.jobMeta}>
              Job ID: <strong>{job?.jobId}</strong>
              {job?.location ? ` · ${job.location}` : ""}
            </p>
            <div className={styles.row}>
              <Form.Group className="mb-3">
                <Form.Label>First name*</Form.Label>
                <Form.Control
                  value={form.firstName}
                  onChange={updateField("firstName")}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Last name*</Form.Label>
                <Form.Control
                  value={form.lastName}
                  onChange={updateField("lastName")}
                  required
                />
              </Form.Group>
            </div>
            <div className={styles.row}>
              <Form.Group className="mb-3">
                <Form.Label>Contact number*</Form.Label>
                <Form.Control
                  value={form.phone}
                  onChange={updateField("phone")}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email*</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={updateField("email")}
                  required
                />
              </Form.Group>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Experience*</Form.Label>
              <Form.Control
                placeholder="e.g. 4 years in B2B sales"
                value={form.experience}
                onChange={updateField("experience")}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Covering letter*</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={form.coveringLetter}
                onChange={updateField("coveringLetter")}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>CV (PDF, DOC, or DOCX)*</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(event) => setCv(event.target.files?.[0] || null)}
                required
              />
            </Form.Group>
            {error ? <p className={styles.error}>{error}</p> : null}
            <div className={styles.actions}>
              <Button variant="outline-secondary" type="button" onClick={resetAndClose}>
                Cancel
              </Button>
              <Button className={styles.submit} type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" /> Submitting
                  </>
                ) : (
                  "Submit application"
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ApplyModal;
